/* eslint-disable no-lonely-if */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash/fp';
import { TableRow } from '@material-ui/core';
import { UnitType } from '../../../../../../generated/graphql';
import { usePriceService } from '../../../../../../utils/hooks/priceService';
import { useUser } from '../../../../../../utils/hooks/auth';
import { TableCell, TextField, Typography } from '../../../../../../ui-kit';
import { localizeEnum } from '../../../../../../utils';
import { PendingListItemFormValue } from '../../types';
import ActionButtons from './ActionButtons';
import AttachButton from './AttachButton';
import UnitSelect from './UnitSelect';
import { connectCardsTableHeadCells } from './constants';

interface ConnectCardsTableRowProps {
    item: PendingListItemFormValue;
    onEdit: (item: PendingListItemFormValue) => void;
    onDelete: () => void;
    onAttach: () => void;
}

interface LocalItem extends Omit<PendingListItemFormValue, 'unitPrice' | 'unitPriceWithoutVat'> {
    unitPrice: string;
    unitPriceWithoutVat: string;
}

const ConnectCardsTableRow = ({ item, onEdit, onDelete, onAttach }: ConnectCardsTableRowProps) => {
    const { t } = useTranslation();
    const { user: { defaultCurrency } } = useUser();
    const { priceService } = usePriceService();

    const formatPrice = (price?: number | null) => {
        if (!isNil(price)) {
            const { precision } = defaultCurrency;
            return priceService.integerToFloatWithPrecision(price, precision).toString();
        }

        return '';
    };

    const parsePrice = (price?: string | null) => {
        let parsedPrice;
        if (price) {
            const { precision } = defaultCurrency;
            parsedPrice = priceService.parseIntegerFromFloatWithPrecision(parseFloat(price), precision);
        }

        return parsedPrice;
    };

    const [isEditing, setIsEditing] = useState(false);
    const [localItem, setLocalItem] = useState<LocalItem>({
        ...item,
        unitPrice: formatPrice(item.unitPrice) || '',
        unitPriceWithoutVat: formatPrice(item.unitPriceWithoutVat) || '',
    });

    useEffect(() => {
        let newLocalItem = {
            ...item,
            unitPrice: formatPrice(item.unitPrice) || '',
            unitPriceWithoutVat: formatPrice(item.unitPriceWithoutVat) || '',
        };

        const { vatRate, stockCard, unitPrice, unitPriceWithoutVat } = item;

        const hasMissingPrice = isNil(unitPrice) || isNil(unitPriceWithoutVat);
        const hasNewStockCard = !!(stockCard?.id && stockCard.id !== localItem.stockCard?.id);

        if (!isNil(vatRate) && (hasMissingPrice || hasNewStockCard)) {
            if (!isNil(unitPrice)) {
                const { netPrice } = priceService.calculateTax(unitPrice, vatRate, defaultCurrency);

                newLocalItem = {
                    ...newLocalItem,
                    unitPriceWithoutVat: formatPrice(netPrice),
                };

                if (!isEditing) {
                    onEdit({
                        ...item,
                        unitPriceWithoutVat: netPrice,
                    });
                }
            } else if (!isNil(unitPriceWithoutVat)) {
                const { priceWithVat } = priceService.calculatePriceWithTax(unitPriceWithoutVat, vatRate, defaultCurrency);

                newLocalItem = {
                    ...newLocalItem,
                    unitPrice: formatPrice(priceWithVat),
                };

                if (!isEditing) {
                    onEdit({
                        ...item,
                        unitPrice: priceWithVat,
                    });
                }
            }
        }

        setLocalItem(newLocalItem);
    }, [item]);

    const handleItemEdit = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const { vatRate, unitPrice, unitPriceWithoutVat } = localItem;

        let newUnitPrice = unitPrice;
        let newUnitPriceWithoutVat = unitPriceWithoutVat;

        if (field === 'unitPrice' && !isNil(vatRate)) {
            const parsedUnitPrice = parsePrice(e.target.value);
            if (parsedUnitPrice) {
                const { netPrice } = priceService.calculateTax(parsedUnitPrice, vatRate, defaultCurrency);
                newUnitPriceWithoutVat = formatPrice(netPrice);
            } else {
                newUnitPriceWithoutVat = '';
            }
        }

        if (field === 'unitPriceWithoutVat' && !isNil(vatRate)) {
            const parsedUnitPriceWithoutVat = parsePrice(e.target.value);
            if (parsedUnitPriceWithoutVat) {
                const { priceWithVat } = priceService.calculatePriceWithTax(parsedUnitPriceWithoutVat, vatRate, defaultCurrency);
                newUnitPrice = formatPrice(priceWithVat);
            } else {
                newUnitPrice = '';
            }
        }

        setLocalItem({
            ...localItem,
            unitPrice: newUnitPrice,
            unitPriceWithoutVat: newUnitPriceWithoutVat,
            [field]: e.target.value,
        });
    };

    const handleStartEditing = () => {
        setIsEditing(true);

        setLocalItem({
            ...item,
            unitPrice: formatPrice(item.unitPrice),
            unitPriceWithoutVat: formatPrice(item.unitPriceWithoutVat),
        });
    };

    const handleConfirm = () => {
        const { unitPrice, unitPriceWithoutVat, quantity, finalAmount } = localItem;

        const newItem = {
            ...localItem,
            unitPrice: parsePrice(unitPrice),
            unitPriceWithoutVat: parsePrice(unitPriceWithoutVat),
        };

        if (quantity && typeof quantity === 'string') {
            newItem.quantity = parseFloat(quantity);
        }

        if (finalAmount && typeof finalAmount === 'string') {
            newItem.finalAmount = parseFloat(finalAmount);
        }

        onEdit(newItem);
        setIsEditing(false);
    };

    return (
        <TableRow tabIndex={-1} key={item.id}>
            {connectCardsTableHeadCells.map((cell, index) => {
                let cellValue: React.ReactNode;

                if (cell.id === 'actions') {
                    cellValue = (
                        <ActionButtons
                            isEditing={isEditing}
                            onEdit={handleStartEditing}
                            onConfirm={handleConfirm}
                            onDelete={onDelete}
                        />
                    );
                } else if (cell.id === 'stockCard') {
                    cellValue = (
                        <AttachButton
                            stockCardName={item.stockCard?.name}
                            onClick={onAttach}
                        />
                    );
                } else {
                    let cellText = localItem[cell.id];

                    if (cell.id === 'unitOfMeasure' && cellText) {
                        cellText = localizeEnum(t)(localItem.unitOfMeasure || '', { UnitType });
                    }

                    if (!isEditing) {
                        if (cell.id === 'unitPriceWithoutVat' && item.unitPriceWithoutVat) {
                            cellText = priceService.toLocaleString(item.unitPriceWithoutVat, defaultCurrency.currencyCode);
                        }

                        if (cell.id === 'unitPrice' && item.unitPrice) {
                            cellText = priceService.toLocaleString(item.unitPrice, defaultCurrency.currencyCode);
                        }

                        cellValue = (
                            <Typography variant='body2' color='textPrimary'>
                                {cellText}
                            </Typography>
                        );
                    } else {
                        if (['unitPrice', 'unitPriceWithoutVat', 'finalAmount'].includes(cell.id)) {
                            const isDisabled = !item.stockCard && cell.id === 'finalAmount';

                            cellValue = (
                                <TextField
                                    onChange={handleItemEdit(cell.id)}
                                    value={cellText}
                                    type='number'
                                    disabled={isDisabled}
                                    margin='none'
                                />
                            );
                        } else if (cell.id === 'unitOfMeasure') {
                            cellValue = (
                                <UnitSelect
                                    stockCard={item.stockCard}
                                    value={localItem[cell.id]}
                                    onChange={handleItemEdit(cell.id)}
                                />
                            );
                        } else {
                            cellValue = (
                                <Typography variant='body2' color='textPrimary'>
                                    {cellText}
                                </Typography>
                            );
                        }
                    }
                }

                return (
                    <TableCell
                        align={cell.align}
                        isSticky={index === 0}
                        key={`${cell.id} - ${item.id}`}
                        style={{ padding: 8, zIndex: index === 0 ? 1 : 'initial' }}
                    >
                        {cellValue}
                    </TableCell>
                );
            })}
        </TableRow>
    );
};

export default ConnectCardsTableRow;
